/**
 * PDP quick quote (raq-v2 buybox v4, 2026-08-06).
 *
 * Posts a schema 3.2.0 payload to the existing RFQ ingest
 * (rfq.metalsrus.ca/submit): one freeform line that carries the product
 * identity as STRUCTURED fields (matched_product_id, product_title,
 * product_sku, product_handle, size_text) plus a real quantity.
 * freeform_text keeps the old "<title> (SKU x): <size>" blob so
 * 3.0.x/3.1.x consumers keep working. submitted_from = 'pdp-quick-quote'
 * so the quote dashboard can attribute these separately from the full form.
 *
 * v4: pickup / delivery is asked, not assumed. A required radio pair sits
 * after size + quantity; Delivery reveals a structured address block that
 * submits as shipping_address (the same shape the full form and the ingest
 * already use), so staff can price freight + taxes on the first pass.
 * v3 hardcoded fulfillment_method 'pickup' with a "Fulfillment not asked"
 * note; that guess only survives as the stale-Liquid fallback below.
 *
 * Back-compat (CDN lag in either direction):
 * - Stale Liquid + this JS: no fulfillment block in the DOM. Falls back to
 *   the v3 payload (pickup + "Fulfillment not asked" note).
 * - New Liquid + stale JS: the fulfillment + address blocks ship hidden and
 *   only the JS that reads them (this file) reveals them, so a stale script
 *   never shows fields it would silently drop. Same trick as the qty field.
 *
 * All customer-facing strings come from data attributes rendered by the
 * Liquid snippet (locale-driven, EN + FR); this file holds no copy.
 */
(() => {
  if (window.__raqQuickQuoteInit) return;
  window.__raqQuickQuoteInit = true;

  // Reveal the fields only this JS knows how to read (see header).
  document.querySelectorAll('[data-qq-qty-wrap][hidden], [data-qq-ff-wrap][hidden]').forEach((el) => el.removeAttribute('hidden'));

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function els(root) {
    return {
      form: root.querySelector('[data-qq-form]'),
      stepbar: root.querySelector('[data-qq-stepbar]'),
      size: root.querySelector('[data-qq-size]'),
      qty: root.querySelector('[data-qq-qty]'),
      ffWrap: root.querySelector('[data-qq-ff-wrap]'),
      addr: root.querySelector('[data-qq-addr]'),
      addr1: root.querySelector('[data-qq-addr1]'),
      addr2: root.querySelector('[data-qq-addr2]'),
      city: root.querySelector('[data-qq-city]'),
      prov: root.querySelector('[data-qq-prov]'),
      postal: root.querySelector('[data-qq-postal]'),
      country: root.querySelector('[data-qq-country]'),
      name: root.querySelector('[data-qq-name]'),
      email: root.querySelector('[data-qq-email]'),
      err: root.querySelector('[data-qq-err]'),
      submit: root.querySelector('[data-qq-submit]'),
      submitLabel: root.querySelector('[data-qq-submit-label]'),
      success: root.querySelector('[data-qq-success]'),
      echo: root.querySelector('[data-qq-echo]'),
    };
  }

  function showErr(e, msg) {
    if (!e.err) return;
    e.err.textContent = msg;
    e.err.hidden = false;
  }

  function selectedFulfillment(root) {
    const checked = root.querySelector('[data-qq-ff]:checked');
    return checked ? checked.value : '';
  }

  function val(el) {
    return (el && el.value ? el.value : '').trim();
  }

  // fulfillment: '' (stale Liquid, block absent), 'pickup', or
  // 'include_shipping'. address is non-null only for include_shipping.
  function buildPayload(root, sizeText, qty, firstName, lastName, email, fulfillment, address) {
    const title = root.getAttribute('data-qq-product-title') || '';
    const sku = root.getAttribute('data-qq-sku') || '';
    const handle = root.getAttribute('data-qq-product-handle') || '';
    const productId = parseInt(root.getAttribute('data-qq-product-id'), 10);
    const skuPart = sku ? ` (SKU ${sku})` : '';
    const noteBase = `Quick quote from the product page: ${title}${skuPart}.`;
    const note = !fulfillment
      ? `${noteBase} Fulfillment not asked; confirm with the customer.`
      : fulfillment === 'include_shipping'
        ? `${noteBase} Customer chose delivery; address attached.`
        : `${noteBase} Customer chose pickup in Dartmouth.`;
    const payload = {
      schema_version: '3.2.0',
      submitted_from: 'pdp-quick-quote',
      was_authenticated: false,
      lang: root.getAttribute('data-qq-lang') || 'en',
      email,
      first_name: firstName,
      last_name: lastName,
      phone: '',
      customer_status: 'New',
      business_name: '',
      ship_to_province: address ? address.province : '',
      timeline: '',
      specific_date: '',
      fulfillment_method: fulfillment || 'pickup',
      cert_requirement: 'None',
      recurring: false,
      additional_notes: note,
      terms_accepted: true,
      terms_accepted_at: new Date().toISOString(),
      promotional_consent: false,
      line_items: [{
        mode: 'freeform',
        // Verbatim blob kept for 3.0.x/3.1.x consumers; the structured
        // fields below are the 3.2.0 source of truth.
        freeform_text: `${title}${skuPart}: ${sizeText}`,
        size_text: sizeText,
        quantity: qty,
        is_specialty_freight: false,
        is_always_raq: true,
        customer_unsure_fields: [],
        matched_product_id: Number.isFinite(productId) ? productId : null,
        product_title: title,
        product_sku: sku,
        product_handle: handle,
      }],
    };
    if (address) {
      // Same shape the full form submits; the ingest validates, persists,
      // and the dashboard + staff email already render it.
      payload.shipping_address = {
        first_name: firstName,
        last_name: lastName,
        company: '',
        address_line_1: address.address_line_1,
        address_line_2: address.address_line_2,
        city: address.city,
        province: address.province,
        postal_code: address.postal_code,
        country: address.country,
      };
    }
    return payload;
  }

  async function submit(root) {
    const e = els(root);
    if (!e.form || !e.submit) return;
    if (e.err) e.err.hidden = true;

    const sizeText = val(e.size);
    const nameRaw = val(e.name);
    const email = val(e.email);

    if (!sizeText || !nameRaw || !EMAIL_RE.test(email)) {
      showErr(e, root.getAttribute('data-qq-str-err-incomplete') || 'Fill in the size, your name, and your email.');
      return;
    }
    // Quantity: whole number, 1 or more. A stale cached render without the
    // field (CDN lag) falls back to 1 so the payload stays valid.
    let qty = 1;
    if (e.qty) {
      const qtyRaw = val(e.qty);
      if (!/^\d+$/.test(qtyRaw) || parseInt(qtyRaw, 10) < 1) {
        showErr(e, root.getAttribute('data-qq-str-err-qty') || 'Enter a whole number for the quantity, 1 or more.');
        return;
      }
      qty = parseInt(qtyRaw, 10);
    }
    // Fulfillment: required when the block rendered (it has no default, so
    // the answer is always an explicit choice). Absent block = stale cached
    // Liquid; fall back to the v3 pickup-guess payload.
    let fulfillment = '';
    let address = null;
    if (e.ffWrap) {
      fulfillment = selectedFulfillment(root);
      if (!fulfillment) {
        showErr(e, root.getAttribute('data-qq-str-err-ff') || 'Choose pickup or delivery.');
        return;
      }
      if (fulfillment === 'include_shipping') {
        address = {
          address_line_1: val(e.addr1),
          address_line_2: val(e.addr2),
          city: val(e.city),
          province: val(e.prov),
          postal_code: val(e.postal).toUpperCase(),
          country: val(e.country) || 'Canada',
        };
        if (!address.address_line_1 || !address.city || !address.province || !address.postal_code) {
          showErr(e, root.getAttribute('data-qq-str-err-addr') || 'Fill in the delivery address: street, city, province, and postal code.');
          return;
        }
      }
    }
    const tokens = nameRaw.split(/\s+/);
    if (tokens.length < 2) {
      showErr(e, root.getAttribute('data-qq-str-err-name') || 'Add a last name or company name.');
      return;
    }
    const firstName = tokens[0];
    const lastName = tokens.slice(1).join(' ');

    const originalLabel = e.submitLabel ? e.submitLabel.textContent : '';
    e.submit.disabled = true;
    if (e.submitLabel) e.submitLabel.textContent = root.getAttribute('data-qq-str-sending') || 'Sending…';

    try {
      const response = await fetch(root.getAttribute('data-qq-endpoint'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildPayload(root, sizeText, qty, firstName, lastName, email, fulfillment, address)),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json().catch(() => ({}));

      // Swap the ask for the confirmation; the page stays a product page.
      if (e.echo) {
        const ref = result && result.reference_number ? ` · ${result.reference_number}` : '';
        const qtyPart = qty > 1 ? `${qty} x ` : '';
        let ffEcho = '';
        if (fulfillment === 'pickup') {
          ffEcho = root.getAttribute('data-qq-str-echo-pickup') || 'Pickup in Dartmouth';
        } else if (fulfillment === 'include_shipping') {
          ffEcho = (root.getAttribute('data-qq-str-echo-delivery') || 'Delivery to %city%').replace('%city%', address.city);
        }
        e.echo.textContent = `${qtyPart}${sizeText}${ref}` + (ffEcho ? ` · ${ffEcho}` : '');
      }
      e.form.hidden = true;
      if (e.stepbar) e.stepbar.hidden = true;
      if (e.success) e.success.hidden = false;
    } catch (err) {
      showErr(e, root.getAttribute('data-qq-str-err-send') || 'That didn’t send. Try again or call 902-468-1112.');
      e.submit.disabled = false;
      if (e.submitLabel) e.submitLabel.textContent = originalLabel;
    }
  }

  document.addEventListener('submit', (evt) => {
    const form = evt.target.closest && evt.target.closest('[data-qq-form]');
    if (!form) return;
    evt.preventDefault();
    const root = form.closest('[data-qq]');
    if (root) submit(root);
  });

  // Delivery reveals the address block; pickup folds it away (values kept
  // in the DOM so flipping back does not wipe a half-typed address).
  document.addEventListener('change', (evt) => {
    const radio = evt.target.closest && evt.target.closest('[data-qq-ff]');
    if (!radio) return;
    const root = radio.closest('[data-qq]');
    if (!root) return;
    const addr = root.querySelector('[data-qq-addr]');
    if (addr) addr.hidden = selectedFulfillment(root) !== 'include_shipping';
  });
})();
